/**
 * Drawing helpers shared by every carousel template.
 */

import { SLIDE_W, SLIDE_H, type SlideStyle } from "./types";

export function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png")
  );
}

/**
 * Top+bottom gradient band, clear in the middle. Used to keep header/footer
 * text readable on a cover slide that has no photo and a flat or transparent
 * background — the PNG is likely to be composited onto some other image or
 * color later, so the text needs its own contrast. `dark` (default) draws
 * black bands for light text; pass `false` when the cover's text has flipped
 * to a dark ink color (e.g. a white/light flat background) so the bands
 * flip to a light, translucent tone instead.
 */
export function drawReadabilityGradient(ctx: CanvasRenderingContext2D, dark = true): void {
  const rgb = dark ? "0,0,0" : "255,255,255";
  const grad = ctx.createLinearGradient(0, 0, 0, SLIDE_H);
  grad.addColorStop(0,    `rgba(${rgb},0.72)`);
  grad.addColorStop(0.38, `rgba(${rgb},0.08)`);
  grad.addColorStop(0.62, `rgba(${rgb},0.08)`);
  grad.addColorStop(1,    `rgba(${rgb},0.70)`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, SLIDE_W, SLIDE_H);
}

/** Curated flat cover-background colors, keyed by the non-"auto"/"transparent" backgroundMode values. */
export const FLAT_BG_COLORS: Record<"light" | "dark" | "accent", string> = {
  light: "#ffffff",
  dark: "#0b0b0c",
  accent: "#2e7d5b",
};

/**
 * Draws `img` covering the full slide (object-fit: cover semantics).
 * `offsetY` (-1..1) slides the crop window vertically: -1 pins the top of
 * the photo, 1 pins the bottom, 0 centers. Never exposes blank edges.
 * `zoom` (>=1) scales the photo further beyond the cover-fit minimum, so
 * the user can crop in tighter than "fill the frame" — 1 = no extra zoom.
 */
export function drawCoverFitImage(ctx: CanvasRenderingContext2D, img: HTMLImageElement, offsetY = 0, zoom = 1): void {
  const scale = Math.max(SLIDE_W / img.naturalWidth, SLIDE_H / img.naturalHeight) * Math.max(1, zoom);
  const dw = img.naturalWidth * scale;
  const dh = img.naturalHeight * scale;
  const slack = (dh - SLIDE_H) / 2; // how far the crop can move before showing edges
  const y = (SLIDE_H - dh) / 2 - Math.max(-1, Math.min(1, offsetY)) * slack;
  ctx.drawImage(img, (SLIDE_W - dw) / 2, y, dw, dh);
}

/**
 * Watermark, bottom-right corner. Called unconditionally at the end of every
 * template draw function; no-ops when the style carries no watermark (the
 * premium path). `dark` flips to an ink color for light templates.
 */
export function drawWatermark(ctx: CanvasRenderingContext2D, style: SlideStyle, dark = false): void {
  if (!style.watermark) return;
  ctx.save();
  ctx.font = `500 22px ${style.fonts.body}`;
  ctx.fillStyle = dark ? "rgba(20,25,20,0.45)" : "rgba(255,255,255,0.55)";
  ctx.textAlign = "right";
  ctx.textBaseline = "alphabetic";
  ctx.fillText(style.watermark.text, SLIDE_W - 28, SLIDE_H - 24);
  ctx.restore();
}

/** Truncates text with an ellipsis so it never overflows `maxW`. */
export function truncateToWidth(ctx: CanvasRenderingContext2D, text: string, maxW: number): string {
  if (ctx.measureText(text).width <= maxW) return text;
  let lo = 0, hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (ctx.measureText(text.slice(0, mid) + "…").width <= maxW) lo = mid;
    else hi = mid - 1;
  }
  return lo > 0 ? text.slice(0, lo) + "…" : "…";
}

/* ── Helpers for the designer cover series (elegant / glass-bar / magazine /
      handwritten / gallery). All five reproduce Figma mocks that use widely
      letter-spaced caps, big serif stat numbers and baked-in swipe cues. ── */

/**
 * Draws text with manual letter tracking (Canvas `letterSpacing` is not
 * available in every browser we support). Returns the x after the last glyph.
 * Assumes ctx.textAlign = "left".
 */
export function drawTrackedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  tracking: number
): number {
  let cx = x;
  for (const ch of text) {
    ctx.fillText(ch, cx, y);
    cx += ctx.measureText(ch).width + tracking;
  }
  return text.length > 0 ? cx - tracking : cx;
}

/** Width of `text` drawn with drawTrackedText at the current ctx font. */
export function measureTracked(ctx: CanvasRenderingContext2D, text: string, tracking: number): number {
  let w = 0;
  for (const ch of text) w += ctx.measureText(ch).width + tracking;
  return text.length > 0 ? w - tracking : 0;
}

/** Right-aligned tracked text; returns the x of the first glyph. */
export function drawTrackedRight(
  ctx: CanvasRenderingContext2D,
  text: string,
  rightX: number,
  y: number,
  tracking: number
): number {
  const startX = rightX - measureTracked(ctx, text, tracking);
  drawTrackedText(ctx, text, startX, y, tracking);
  return startX;
}

/**
 * Splits a stat value like "22 вида" into the big serif number ("22") and
 * its small-caps label ("вида"). Values without a leading number render
 * entirely in the label style.
 */
export function splitStatValue(value: string): { num: string | null; label: string } {
  const m = value.trim().match(/^([\d.,:]+)\s*(.*)$/);
  if (!m) return { num: null, label: value.trim() };
  return { num: m[1], label: m[2] };
}

/**
 * Big-number stat row shared by the designer covers: each filled stat is a
 * segment — leading number drawn large at `numBase` with its label below at
 * `labelBase`; values without a leading number (e.g. "Алматы") draw the whole
 * value in `textFont` on the number line so nothing looks sunken. Hairline
 * dividers separate segments. Returns the x where the row ends.
 */
export function drawStatRow(
  ctx: CanvasRenderingContext2D,
  stats: { value: string }[],
  spec: {
    x0: number;
    numBase: number;
    labelBase: number;
    numFont: string;
    textFont: string;   // for text-only stat values, drawn at numBase
    labelFont: string;
    labelTracking: number;
    lowercaseLabels?: boolean;
    labelAlpha: number;
    solid: string;
    rgb: string;
    divider: { top: number; height: number; alpha: number };
    segGap: number;     // clear space on each side of a divider
  }
): number {
  const filled = stats.filter((s) => s.value.trim() !== "");
  let x = spec.x0;
  filled.forEach((seg, i) => {
    const { num, label } = splitStatValue(seg.value);
    const labelText = spec.lowercaseLabels ? label.toLowerCase() : label.toUpperCase();

    let numW = 0;
    if (num) {
      ctx.font = spec.numFont;
      numW = ctx.measureText(num).width;
    } else if (labelText) {
      // text-only stat: the whole value lives on the number line
      ctx.font = spec.textFont;
      numW = ctx.measureText(spec.lowercaseLabels ? seg.value.trim() : seg.value.trim().toUpperCase()).width;
    }
    ctx.font = spec.labelFont;
    const labelW = num ? measureTracked(ctx, labelText, spec.labelTracking) : 0;
    const segW = Math.max(numW, labelW);

    if (i > 0) {
      ctx.fillStyle = `rgba(${spec.rgb},${spec.divider.alpha})`;
      ctx.fillRect(x - spec.segGap, spec.divider.top, 1, spec.divider.height);
    }
    if (num) {
      ctx.font = spec.numFont;
      ctx.fillStyle = spec.solid;
      ctx.fillText(num, x, spec.numBase);
      if (labelText) {
        ctx.font = spec.labelFont;
        ctx.fillStyle = `rgba(${spec.rgb},${spec.labelAlpha})`;
        drawTrackedText(ctx, labelText, x, spec.labelBase, spec.labelTracking);
      }
    } else {
      ctx.font = spec.textFont;
      ctx.fillStyle = spec.solid;
      ctx.fillText(spec.lowercaseLabels ? seg.value.trim() : seg.value.trim().toUpperCase(), x, spec.numBase);
    }
    x += segW + spec.segGap * 2;
  });
  return filled.length > 0 ? x - spec.segGap * 2 : spec.x0;
}

/**
 * Long swipe arrow "→" pointing right, tip at (tipX, y).
 * Matches the hand-tuned stroke arrows from the designer mocks.
 */
export function drawLongArrow(
  ctx: CanvasRenderingContext2D,
  tipX: number,
  y: number,
  length: number,
  strokeW: number,
  head: number
): void {
  ctx.save();
  ctx.lineWidth = strokeW;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(tipX - length, y);
  ctx.lineTo(tipX, y);
  ctx.moveTo(tipX - head, y - head);
  ctx.lineTo(tipX, y);
  ctx.lineTo(tipX - head, y + head);
  ctx.stroke();
  ctx.restore();
}

/**
 * Two edge bands fading from a dark tint at the very top/bottom edges to
 * fully transparent `height` px inward, leaving the middle of the photo
 * untouched — keeps cover text readable without flattening the whole image
 * the way drawReadabilityGradient's full-height ramp does.
 */
export function drawEdgeScrims(ctx: CanvasRenderingContext2D, height = 450, alpha = 0.78): void {
  const top = ctx.createLinearGradient(0, 0, 0, height);
  top.addColorStop(0, `rgba(0,0,0,${alpha})`);
  top.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = top;
  ctx.fillRect(0, 0, SLIDE_W, height);

  const bottom = ctx.createLinearGradient(0, SLIDE_H - height, 0, SLIDE_H);
  bottom.addColorStop(0, "rgba(0,0,0,0)");
  bottom.addColorStop(1, `rgba(0,0,0,${alpha})`);
  ctx.fillStyle = bottom;
  ctx.fillRect(0, SLIDE_H - height, SLIDE_W, height);
}

/**
 * Hand-drawn-feeling swipe arrow: a gentle S-curve running right, tip at
 * (tipX, y). Pairs with the script-font swipe cue on the cover.
 */
export function drawCurvedArrow(
  ctx: CanvasRenderingContext2D,
  tipX: number,
  y: number,
  length: number,
  strokeW = 3,
  head = 17
): void {
  const x0 = tipX - length;
  ctx.save();
  ctx.lineWidth = strokeW;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(x0, y);
  ctx.bezierCurveTo(x0 + length * 0.30, y + 26, x0 + length * 0.66, y - 26, tipX, y - 5);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(tipX - head, y - head + 1);
  ctx.lineTo(tipX, y - 5);
  ctx.lineTo(tipX - head + 3, y + head - 8);
  ctx.stroke();
  ctx.restore();
}

/** Chevron "›" centered at (cx, cy) with the given half-width/half-height. */
export function drawChevron(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  halfW: number,
  halfH: number,
  strokeW: number
): void {
  ctx.save();
  ctx.lineWidth = strokeW;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(cx - halfW, cy - halfH);
  ctx.lineTo(cx + halfW, cy);
  ctx.lineTo(cx - halfW, cy + halfH);
  ctx.stroke();
  ctx.restore();
}

/** Approximate cap-height ratio of our display serifs. */
export const SERIF_CAP_RATIO = 0.71;

export interface SerifHeadlineFit {
  /** Font size actually used after shrink-to-fit (≤ the requested size). */
  usedSize: number;
  /** Wrapped lines, already clamped to the 2-line maximum. */
  lines: string[];
  /** Baseline-to-baseline distance. */
  lineH: number;
  /** y of the visual cap top of the first line. */
  capTop: number;
  /** Alphabetic baseline y of the first line. */
  firstBaseline: number;
  /** Alphabetic baseline y of the last line. */
  lastBaseline: number;
}

/**
 * Wrap + shrink-to-fit maths for a serif headline, with no drawing. Split out
 * of drawSerifHeadline so the live editor's HTML overlay can position an
 * <input> over the exact same lines the canvas would paint — the overlay
 * needs `usedSize`/`lineH`, which shrink-to-fit only knows after measuring.
 */
export function fitSerifHeadline(
  ctx: CanvasRenderingContext2D,
  text: string,
  firstBaselineIn: number,
  maxW: number,
  size: number,
  fontFamily: string,
  opts: { italic?: boolean; weight?: number; capTop?: number } = {}
): SerifHeadlineFit {
  const { italic = false, weight = 400, capTop } = opts;
  const style = italic ? "italic " : "";
  const maxLines = 2;

  let usedSize = size;
  let lines: string[] = [];
  for (let attempt = 0; attempt < 4; attempt++) {
    ctx.font = `${style}${weight} ${usedSize}px ${fontFamily}`;
    lines = [];
    let cur = "";
    let widest = 0;
    for (const word of text.split(/\s+/)) {
      const test = cur ? `${cur} ${word}` : word;
      if (cur && ctx.measureText(test).width > maxW) {
        widest = Math.max(widest, ctx.measureText(cur).width);
        lines.push(cur);
        cur = word;
      } else {
        cur = test;
      }
    }
    lines.push(cur);
    widest = Math.max(widest, ...lines.map((l) => ctx.measureText(l).width));
    if (lines.length <= maxLines && widest <= maxW) break;
    usedSize = Math.max(40, Math.floor(usedSize * Math.min(maxW / widest, lines.length > maxLines ? 0.8 : 1)));
  }

  // Keep the visual top of the caps anchored when the size shrank.
  const top = capTop ?? firstBaselineIn - size * SERIF_CAP_RATIO;
  const first = top + usedSize * SERIF_CAP_RATIO;
  const lineH = usedSize * 0.95;
  const clamped = lines.slice(0, maxLines);
  return {
    usedSize,
    lines: clamped,
    lineH,
    capTop: top,
    firstBaseline: first,
    lastBaseline: first + (clamped.length - 1) * lineH,
  };
}

/**
 * Serif headline with word wrap (max 2 lines) and shrink-to-fit: the font
 * size is reduced when a single word is wider than `maxW` or the text needs
 * more than 2 lines. Draws with alphabetic baselines starting at
 * `firstBaseline` (adjusted proportionally when the size shrinks) and returns
 * the baseline y of the LAST drawn line.
 */
export function drawSerifHeadline(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  firstBaseline: number,
  maxW: number,
  size: number,
  fontFamily: string,
  opts: { italic?: boolean; weight?: number; capTop?: number; dryRun?: boolean } = {}
): number {
  const { italic = false, weight = 400, dryRun = false } = opts;
  const fit = fitSerifHeadline(ctx, text, firstBaseline, maxW, size, fontFamily, opts);
  ctx.font = `${italic ? "italic " : ""}${weight} ${fit.usedSize}px ${fontFamily}`;
  ctx.textBaseline = "alphabetic";
  if (!dryRun) {
    fit.lines.forEach((line, i) => ctx.fillText(line, x, fit.firstBaseline + i * fit.lineH));
  }
  return fit.lastBaseline;
}

/**
 * Background fill shared by the designer covers: photo → cover-fit; curated
 * flat color → fill; "auto" → the dark field-green gradient; "transparent" →
 * nothing. Returns true when the cover text must flip to dark ink (the
 * "light" flat background).
 */
export function drawDesignerBackdrop(
  ctx: CanvasRenderingContext2D,
  data: {
    photo?: HTMLImageElement | null;
    backgroundMode?: "auto" | "transparent" | "light" | "dark" | "accent";
    photoOffsetY?: number;
    photoZoom?: number;
  }
): boolean {
  const textOnLight = !data.photo && data.backgroundMode === "light";
  if (data.photo) {
    drawCoverFitImage(ctx, data.photo, data.photoOffsetY ?? 0, data.photoZoom ?? 1);
  } else if (data.backgroundMode && data.backgroundMode in FLAT_BG_COLORS) {
    ctx.fillStyle = FLAT_BG_COLORS[data.backgroundMode as keyof typeof FLAT_BG_COLORS];
    ctx.fillRect(0, 0, SLIDE_W, SLIDE_H);
  } else if (data.backgroundMode !== "transparent") {
    const bg = ctx.createLinearGradient(0, 0, 0, SLIDE_H);
    bg.addColorStop(0, "#20301f");
    bg.addColorStop(1, "#0e1710");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, SLIDE_W, SLIDE_H);
  }
  return textOnLight;
}

/**
 * The mock scrim: rgba(33,33,33,0.78) fading to transparent. `fromTop: true`
 * paints y=0..h (dense at the top); false paints the mirrored band rising
 * from the bottom edge. Flips to a light scrim when the text is dark ink.
 */
export function drawMockScrim(ctx: CanvasRenderingContext2D, h: number, fromTop: boolean, dark = true): void {
  const rgb = dark ? "33,33,33" : "235,238,235";
  const y0 = fromTop ? 0 : SLIDE_H;
  const y1 = fromTop ? h : SLIDE_H - h;
  const grad = ctx.createLinearGradient(0, y0, 0, y1);
  grad.addColorStop(0, `rgba(${rgb},0.78)`);
  grad.addColorStop(1, `rgba(135,135,135,0)`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, Math.min(y0, y1), SLIDE_W, h);
}

/** Greedy word-wrap at the current ctx font, clamped to `maxLines`. Shared by
 * wrapTopDown (draws) and coverTextLayout in field-diary.ts (measures only,
 * to position the subtitle below a dynamically-wrapped headline). */
export function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxW: number, maxLines: number): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (cur && ctx.measureText(test).width > maxW) { lines.push(cur); cur = w; }
    else cur = test;
  }
  lines.push(cur);
  return lines.slice(0, maxLines);
}

/** Word-wraps downward from topY, max `maxLines`; returns the y below the last line. */
export function wrapTopDown(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  topY: number,
  maxW: number,
  lineH: number,
  maxLines: number
): number {
  const clamped = wrapLines(ctx, text, maxW, maxLines);
  ctx.textBaseline = "top";
  let y = topY;
  for (const line of clamped) { ctx.fillText(line, x, y); y += lineH; }
  return y;
}
